import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Globe, FileText, TrendingUp, MapPin, AlertCircle, CheckCircle2, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPortalPage() {
  const { slug, token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    if (!slug || !token) {
      setState({ loading: false, error: "This portal link is invalid or has been revoked.", data: null });
      return () => { cancelled = true; };
    }
    axios.get(`${API}/portal/${slug}/${token}`).then(({ data }) => {
      if (!cancelled) setState({ loading: false, error: null, data });
    }).catch((e) => {
      if (cancelled) return;
      const msg = e?.response?.data?.detail || "This portal link is invalid or has been revoked.";
      setState({ loading: false, error: typeof msg === "string" ? msg : "Portal not found", data: null });
    });
    return () => { cancelled = true; };
  }, [slug, token]);

  // Update document title only when data changes (was running on every render)
  useEffect(() => {
    if (state.data) {
      document.title = `${state.data.client.name} · ${state.data.branding.brand_name}`;
    }
  }, [state.data]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#09090B] grid place-items-center" data-testid="portal-loading">
        <Loader2 className="w-7 h-7 animate-spin text-[#0055FF]" />
      </div>
    );
  }
  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-[#09090B] grid place-items-center px-6" data-testid="portal-error">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="font-display text-2xl text-zinc-100 mb-2">Portal not available</h1>
          <p className="text-sm text-zinc-500">{state.error}</p>
        </div>
      </div>
    );
  }

  const { branding, client, projects, generated_at } = state.data;
  const primary = branding.primary_color || "#0055FF";

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100" data-testid="portal-page" style={{ ['--brand']: primary }}>
      <header className="border-b border-zinc-800 bg-[#0B0B0E]/90 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-4">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={branding.brand_name} className="h-10 w-auto object-contain bg-white rounded-sm p-1" data-testid="portal-logo"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="w-10 h-10 rounded-sm grid place-items-center" style={{ background: primary }}>
              <Zap className="w-5 h-5 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-semibold truncate" data-testid="portal-brand-name">{branding.brand_name}</div>
            <div className="text-xs text-zinc-500 truncate">Client report for <span className="text-zinc-300" data-testid="portal-client-name">{client.name}</span></div>
          </div>
          {branding.support_email && (
            <a href={`mailto:${branding.support_email}`} className="hidden sm:inline-block text-sm hover:underline" style={{ color: primary }} data-testid="portal-support-link">
              {branding.support_email}
            </a>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        <section>
          <span className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Performance overview</span>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {client.name}'s growth report
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            A live snapshot of every project we're optimising for {client.name}. SEO scores, local rank, and content output across all properties.
          </p>
        </section>

        {projects.length === 0 ? (
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8 text-center" data-testid="portal-no-projects">
            <Globe className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No projects are linked to this report yet.</p>
            <p className="text-xs text-zinc-600 mt-1">Your account manager will attach properties soon.</p>
          </div>
        ) : (
          <section className="space-y-6">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} primary={primary} />
            ))}
          </section>
        )}
      </main>

      <footer className="border-t border-zinc-800 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <div>
            {branding.footer_text || `© ${new Date().getFullYear()} ${branding.brand_name}. All rights reserved.`}
          </div>
          <div className="flex items-center gap-3">
            <span>Updated {new Date(generated_at).toLocaleString()}</span>
            {!branding.hide_powered_by && (
              <span data-testid="portal-powered-by">
                Powered by <span style={{ color: primary }}>RankPilot AI</span>
              </span>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

function ProjectCard({ project, primary }) {
  const audit = project.latest_audit;
  const rank = project.latest_local_rank;
  const nap = project.latest_nap_audit;

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden" data-testid={`portal-project-${project.id}`}>
      <div className="px-5 py-4 border-b border-zinc-800 flex flex-wrap items-center gap-3">
        <div className="w-9 h-9 rounded-sm grid place-items-center" style={{ background: `${primary}1f`, color: primary, border: `1px solid ${primary}55` }}>
          <Globe className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-medium truncate">{project.name}</div>
          <a href={`https://${project.domain}`} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:underline truncate block">
            {project.domain}
          </a>
        </div>
        {project.industry && (
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-800 rounded-sm px-2 py-1">{project.industry}</span>
        )}
      </div>

      <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-800">
        {/* SEO score */}
        <div className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> SEO Score</div>
          {audit ? (
            <>
              <div className="flex items-end gap-2">
                <span className="font-display text-4xl font-semibold" style={{ color: primary }}>{audit.score}</span>
                <span className="text-sm text-zinc-500 mb-1">/ 100 · {audit.grade}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-2 line-clamp-3 leading-relaxed">{audit.summary}</p>
            </>
          ) : (
            <div className="text-sm text-zinc-600">No audit run yet.</div>
          )}
        </div>

        {/* Local rank */}
        <div className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Local rank</div>
          {rank ? (
            <>
              <div className="flex items-end gap-2">
                <span className="font-display text-4xl font-semibold" style={{ color: primary }}>
                  {rank.in_local_pack ? `#${rank.local_pack_position}` : (rank.organic_position ? `#${rank.organic_position}` : "—")}
                </span>
                <span className="text-sm text-zinc-500 mb-1">{rank.in_local_pack ? "Local Pack" : rank.organic_position ? "Organic" : "Not ranked"}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-2 truncate">"{rank.keyword}" · {rank.location}</p>
            </>
          ) : (
            <div className="text-sm text-zinc-600">No rank check yet.</div>
          )}
        </div>

        {/* Content + NAP */}
        <div className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><FileText className="w-3 h-3" /> Content + NAP</div>
          <div className="flex items-end gap-2">
            <span className="font-display text-4xl font-semibold" style={{ color: primary }}>{project.blog_posts_count || 0}</span>
            <span className="text-sm text-zinc-500 mb-1">blog posts</span>
          </div>
          {nap ? (
            <p className="text-xs mt-2 flex items-center gap-1.5">
              {nap.score >= 80 ? <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" /> : <AlertCircle className="w-3.5 h-3.5 text-amber-400" />}
              <span className="text-zinc-500">NAP consistency: <span className="text-zinc-300">{nap.score}/100</span></span>
            </p>
          ) : (
            <p className="text-xs text-zinc-600 mt-2">No NAP audit yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
